import { GraphInputHelpers, ONE_DAY_MS } from '@/fetching/graphql/GraphInputHelpers';
import {
    VisitDatetimeField,
    VisitOutcome,
    VisitState,
    createEffectiveDateInRangeFilter,
} from '@/types/visit';
import { EpisodeStatus, Ordering } from 'gql-types';

import {
    ContainerDefinitionType,
    DEFAULT_CONTAINER_FILTER_WINDOW_DAYS,
    defaultEpisodeOfCareRowToLink,
    defaultEpisodeVisitsRowToLink,
    defaultVisitsRowToLink,
    makeContainerDefinition,
} from '../definitions';
import {
    EpisodeOfCareDashboardContainer,
    PatientDashboardContainer,
    RootEntity,
    VisitDashboardContainer,
} from '../types';
import { HH_CONTAINER_KEYS } from './containerKeys/containerKeys';
import { IDashboardVariationConfig } from './types';

/**
 * Home Health dashboard config.
 *
 * Modeled on the Penn Medicine at Home WorkQueues spec
 */

/**
 * Services that count as "reassessment" for the Pending Reassessment
 * queue. Includes both recert and reassessment service codes per Adam
 * and should be stable.
 */
const HH_REASSESSMENT_SERVICE_NAMES = [
    'OT Reassessment',
    'OT Recert',
    'OT ROC/Recert',
    'PT Reassessment',
    'PT Recert',
    'PT ROC/Recert',
    'SN Recert',
    'SN ROC/Recert',
    'ST Reassessment',
    'ST Recert',
    'ST ROC/Recert',
];

// The patient needs a clinician of a certain discipline added to their care team
export const HH_INCOMPLETE_CARE_TEAM_CONTAINER: PatientDashboardContainer = {
    key: HH_CONTAINER_KEYS.incompleteCareTeam,
    label: 'Incomplete Care Team',
    defaultEnabled: true,
    hoverText: 'Patients that need care teams assigned.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: {
                isActiveByDate: { exact: true },
                isActiveField: { exact: true },
                hasVisits: {
                    disciplineCoveredByCareTeam: { exact: false },
                    outcome: { exact: VisitOutcome.NONE },
                },
            },
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients with incomplete care teams.',
        // TODO(BE-304): Spec wants "sort by number of days since SOC
        // completion date / episode start date, oldest first". That
        // requires sorting patients by a property of a filtered subset
        // of their visits. Using firstName ASC as a placeholder until
        // BE-304 lands.
        ordering: { firstName: Ordering.ASC },
    },
};

// A SOC visit exists but has not been scheduled/processed
export const HH_SOC_CONTAINER: VisitDashboardContainer = {
    key: HH_CONTAINER_KEYS.soc,
    label: 'SOCs',
    defaultEnabled: true,
    hoverText: 'Start of Care visits that need scheduling.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            services: { name: { iContains: 'soc' } },
            needsAssignment: { exact: true },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No SOC visits to schedule.',
        ordering: { fulfillmentWindowEndDate: Ordering.ASC },
    },
};

// A ROC visit exists but has not been processed
export const HH_ROC_CONTAINER: VisitDashboardContainer = {
    key: HH_CONTAINER_KEYS.roc,
    label: 'ROCs',
    defaultEnabled: true,
    hoverText: 'Resumption of Care visits that need scheduling.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            services: { name: { iContains: 'roc' } },
            needsAssignment: { exact: true },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No ROC visits to schedule.',
        ordering: { fulfillmentWindowEndDate: Ordering.ASC },
    },
};

// An Eval visit exists but has not been processed
export const HH_EVAL_CONTAINER: VisitDashboardContainer = {
    key: HH_CONTAINER_KEYS.eval,
    label: 'Evals',
    defaultEnabled: true,
    hoverText: 'Eval visits that need scheduling.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            services: { name: { iContains: 'eval' } },
            needsAssignment: { exact: true },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No Eval visits to schedule.',
        ordering: { fulfillmentWindowEndDate: Ordering.ASC },
    },
};

// Visits that need to be scheduled and are nearing the end of their fulfillment window
const HH_EXPIRING_FULFILLMENT_WINDOW_CONTAINER: VisitDashboardContainer = {
    key: HH_CONTAINER_KEYS.expiringFulfillmentWindow,
    label: 'Expiring Fulfillment Windows',
    defaultEnabled: true,
    hoverText: 'Visits with expiring fulfillment windows.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: {
                isActive: { exact: true },
            },
            needsAssignment: { exact: true },
            fulfillmentWindowEndDate: {
                gte: GraphInputHelpers.date(
                    new Date(Date.now() - DEFAULT_CONTAINER_FILTER_WINDOW_DAYS * ONE_DAY_MS),
                ),
                lte: GraphInputHelpers.date(new Date(Date.now() + 3 * ONE_DAY_MS)),
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No visits with expiring fulfillment windows.',
        ordering: { fulfillmentWindowEndDate: Ordering.ASC },
    },
};

// Patients with visits that still need a service, clinician, or date
const HH_PATIENTS_NEEDING_SCHEDULING_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: HH_CONTAINER_KEYS.patientsNeedingScheduling,
    label: 'Patients Needing Scheduling',
    defaultEnabled: true,
    hoverText: 'Patients that need scheduling.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            patient: { isActive: { exact: true } },
            endDate: {
                gte: GraphInputHelpers.date(new Date(Date.now() - 14 * ONE_DAY_MS)),
            },
            hasVisits: { needsAssignment: { exact: true } },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No patients to schedule.',
        // TODO
        // sorting: "fulfillment window end date, soonest first"
        // this is min(visit.fulfillmentWindowEndDate) across the episode?
        // So we cant use episode startDate ASC as northstar does?
        ordering: { startDate: Ordering.ASC },
    },
};

// Visits that are past their scheduled time and have not been started
export const HH_OVERDUE_VISITS_CONTAINER = makeContainerDefinition({
    type: ContainerDefinitionType.OVERDUE_EPISODE_VISIT,
    key: HH_CONTAINER_KEYS.overdueVisits,
    defaultEnabled: true,
});

// No-show visits in Axle that are not appropriately flagged as such in the EMR
export const HH_PATIENT_NO_SHOWS_CONTAINER: VisitDashboardContainer = {
    key: HH_CONTAINER_KEYS.patientNoShows,
    label: 'Patient No-Shows',
    defaultEnabled: true,
    hoverText: 'Patient no-shows requiring attention.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            patient: { isActive: { exact: true } },
            startDatetime: {
                gte: GraphInputHelpers.datetime(
                    new Date(Date.now() - DEFAULT_CONTAINER_FILTER_WINDOW_DAYS * ONE_DAY_MS),
                ),
            },
            state: { exact: VisitState.IN_REVIEW },
            outcome: { exact: VisitOutcome.PATIENT_DID_NOT_SHOW },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No patient no-shows requiring attention.',
        ordering: { startDatetime: Ordering.DESC },
    },
};

// The episode window is nearing its end but no recert or discharge visit has been scheduled
export const HH_PENDING_END_OF_EPISODE_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: HH_CONTAINER_KEYS.pendingEndOfEpisode,
    label: 'Pending End of Episode',
    defaultEnabled: true,
    hoverText: 'Episodes ending soon with no recert or discharge scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            patient: { isActive: { exact: true } },
            endDate: {
                range: {
                    start: GraphInputHelpers.date(new Date()),
                    end: GraphInputHelpers.date(new Date(Date.now() + 10 * ONE_DAY_MS)),
                },
            },
            // "There is no recert visit (that's not missing service or
            // clinician or date) or discharge visit (that's not missing
            // service or clinician or date)"
            // so... NOT (any visit is a recert/discharge that's scheduled or done?
            // scheduled or done? Outcome NONE means scheduled-pending,
            // COMPLETED means already done?).
            visits: {
                NOT: {
                    services: { name: { iContains: 'recert' } },
                    outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    OR: {
                        services: { name: { iContains: 'discharge' } },
                        outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
                    },
                },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No episodes pending end of care.',
        ordering: { endDate: Ordering.ASC },
    },
};

// A patient in a transfer status has scheduled visits
export const HH_TRANSFERS_CONTAINER: PatientDashboardContainer = {
    key: HH_CONTAINER_KEYS.transfers,
    label: 'Transfers',
    defaultEnabled: true,
    hoverText: 'Patients on hold with scheduled future visits.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasEpisodes: { status: { exact: EpisodeStatus.ON_HOLD } },
            hasVisits: {
                outcome: { exact: VisitOutcome.NONE },
                AND: createEffectiveDateInRangeFilter(
                    new Date(),
                    new Date(Date.now() + DEFAULT_CONTAINER_FILTER_WINDOW_DAYS * ONE_DAY_MS),
                ),
            },
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No transfers requiring attention.',
        ordering: { fullName: Ordering.ASC },
        // TODO:
        // sorting: "earliest date of upcoming visits"
        // something like this?
        // ordering: { nextUpcomingVisitStart: Ordering.ASC },
    },
};

// A patient has been discharged but still has visits in Axle
export const HH_DISCHARGE_WITH_PENDING_VISITS_CONTAINER: PatientDashboardContainer = {
    key: HH_CONTAINER_KEYS.dischargeWithPendingVisits,
    label: 'Discharge with Pending Visits',
    defaultEnabled: true,
    hoverText: 'Discharged patients with pending future visits.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: false },
            hasVisits: {
                outcome: { exact: VisitOutcome.NONE },
                AND: createEffectiveDateInRangeFilter(
                    new Date(),
                    new Date(Date.now() + DEFAULT_CONTAINER_FILTER_WINDOW_DAYS * ONE_DAY_MS),
                ),
            },
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No discharges with pending visits.',
        ordering: { fullName: Ordering.ASC },
        // TODO:
        // sorting: "earliest date of upcoming visits"
        // same derived-field issue as Transfers (#10).
        // ordering: { nextUpcomingVisitStart: Ordering.ASC },
    },
};

// Patients that have visits where the assigned clinician is inactive
const HH_INACTIVE_CLINICIAN_VISITS_CONTAINER: VisitDashboardContainer = {
    key: HH_CONTAINER_KEYS.inactiveClinicianVisits,
    label: 'Inactive Clinicians',
    defaultEnabled: true,
    hoverText: 'Visits assigned to inactive clinicians.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            outcome: { exact: VisitOutcome.NONE },
            assignedClinicians: { isActive: { exact: false } },
            effectiveDate: {
                gte: GraphInputHelpers.date(
                    new Date(Date.now() - DEFAULT_CONTAINER_FILTER_WINDOW_DAYS * ONE_DAY_MS),
                ),
            },
        },
        rowToLink: defaultEpisodeVisitsRowToLink,
        emptyTableText: 'No visits assigned to inactive clinicians.',
        ordering: { effectiveDate: Ordering.ASC },
    },
};

// Patients that have no future scheduled appointments
const HH_NO_FUTURE_APPOINTMENTS_CONTAINER: PatientDashboardContainer = {
    key: HH_CONTAINER_KEYS.noFutureAppointments,
    label: 'No Future Appointments',
    defaultEnabled: true,
    hoverText: 'Active patients with no future appointments scheduled.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            NOT: {
                hasVisits: {
                    outcome: { exact: VisitOutcome.NONE },
                    AND: createEffectiveDateInRangeFilter(
                        new Date(),
                        new Date(Date.now() + DEFAULT_CONTAINER_FILTER_WINDOW_DAYS * ONE_DAY_MS),
                    ),
                },
            },
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No active patients without future appointments.',
        ordering: { fullName: Ordering.ASC },
        // TODO:
        // sorting: "dates since last visit with the longest duration first"
        // something like this?
        // ordering: { lastCompletedVisitDate: Ordering.ASC },
    },
};

// SOC, ROC, and Eval visits that don't have an associated order
export const HH_COMPLETED_VISITS_NOT_MAPPED_TO_ORDERS_CONTAINER: VisitDashboardContainer = {
    key: HH_CONTAINER_KEYS.completedVisitsNotMappedToOrders,
    label: 'Completed SOC/ROC/Evals Not Mapped to Orders',
    defaultEnabled: true,
    hoverText: 'Completed SOC/ROC/Eval visits with no linked order.',
    heavyQuery: true,
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            outcome: { exact: VisitOutcome.COMPLETED },
            hasOrder: { exact: false },
            services: {
                name: { iContains: 'soc' },
                OR: {
                    name: { iContains: 'roc' },
                    OR: {
                        name: { iContains: 'eval' },
                    },
                },
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No completed SOC/ROC/Eval visits unlinked to orders.',
        ordering: { startDatetime: Ordering.ASC },
    },
};

//Evals that are scheduled or completed prior to the patient's SOC
export const HH_EVALS_BEFORE_SOCS_CONTAINER: VisitDashboardContainer = {
    key: HH_CONTAINER_KEYS.evalsBeforeSocs,
    label: 'Evals Before SOCs',
    defaultEnabled: true,
    hoverText: 'Evals scheduled or completed before the patient’s SOC.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.VISIT,
        filters: {
            services: { name: { iContains: 'eval' } },
            outcome: { inList: [VisitOutcome.NONE, VisitOutcome.COMPLETED] },
            relatedVisitCondition: {
                exists: true,
                filter: {
                    services: { name: { iContains: 'soc' } },
                    outcome: { exact: VisitOutcome.NONE },
                    effectiveDate: { gte: GraphInputHelpers.date(new Date()) },
                },
                timeConstraints: [
                    {
                        field: VisitDatetimeField.EFFECTIVE_DATE,
                        relativeTo: VisitDatetimeField.EFFECTIVE_DATE,
                        gtOffsetMinutes: 0,
                    },
                ],
                samePatient: true,
            },
        },
        rowToLink: defaultVisitsRowToLink,
        emptyTableText: 'No Evals scheduled before SOCs.',
        ordering: { startDatetime: Ordering.ASC },
    },
};

// A patient with more than 1 visit on an episode with a service code of OASIS Discharge, recert
const HH_MULTIPLE_OASIS_PER_EPISODE_CONTAINER: EpisodeOfCareDashboardContainer = {
    key: HH_CONTAINER_KEYS.multipleOasisPerEpisode,
    label: 'Multiple OASIS Discharge/Recert per Episode',
    defaultEnabled: true,
    hoverText: 'Episodes with more than one OASIS Discharge or Recert visit pending.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.EPISODE_OF_CARE,
        filters: {
            isActiveByDate: { exact: true },
            isActiveField: { exact: true },
            hasVisits: {
                services: { name: { iContains: 'agency discharge' } },
                outcome: { exact: VisitOutcome.NONE },
                relatedVisitCondition: {
                    exists: true,
                    filter: {
                        services: { name: { iContains: 'agency discharge' } },
                        outcome: { exact: VisitOutcome.NONE },
                    },
                    sameEpisode: true,
                },
            },
            OR: {
                hasVisits: {
                    services: { name: { iContains: 'recert' } },
                    outcome: { exact: VisitOutcome.NONE },
                    relatedVisitCondition: {
                        exists: true,
                        filter: {
                            services: { name: { iContains: 'recert' } },
                            outcome: { exact: VisitOutcome.NONE },
                        },
                        sameEpisode: true,
                    },
                },
            },
        },
        rowToLink: defaultEpisodeOfCareRowToLink,
        emptyTableText: 'No episodes with multiple OASIS Discharge or Recert visits.',
        ordering: { startDate: Ordering.ASC },
    },
};

// The reassessment window is nearing 30 days but no reassessment visit has been scheduled
export const HH_PENDING_REASSESSMENT_CONTAINER: PatientDashboardContainer = {
    key: HH_CONTAINER_KEYS.pendingReassessment,
    label: 'Pending Reassessment',
    defaultEnabled: true,
    hoverText: 'Patients due for reassessment with none scheduled.',
    snoozable: true,
    tableConfig: {
        entity: RootEntity.PATIENT,
        filters: {
            isActive: { exact: true },
            hasVisits: {
                services: { name: { inList: HH_REASSESSMENT_SERVICE_NAMES } },
            },
            // TODO:
            // still need
            // "today >= 25 days after max(visits.completedAt where service in
            // HH_REASSESSMENT_SERVICE_NAMES)" AND
            // "no upcoming visit in same service list within next 5 days"
        },
        rowToLink: ({ id }: { id: string }) => `/patients/${id}/scheduling`,
        emptyTableText: 'No patients pending reassessment.',
        ordering: { fullName: Ordering.ASC },
        // TODO:
        // sorting: "days since the previous reassessment visit, with the highest number first"
        // something like this?
        // ordering: { lastReassessmentVisitDate: Ordering.DESC },
    },
};

export const HOME_HEALTH_DASHBOARD_CONFIG: IDashboardVariationConfig = {
    containers: [
        HH_INCOMPLETE_CARE_TEAM_CONTAINER,
        HH_SOC_CONTAINER,
        HH_ROC_CONTAINER,
        HH_EVAL_CONTAINER,
        HH_EXPIRING_FULFILLMENT_WINDOW_CONTAINER,
        HH_PATIENTS_NEEDING_SCHEDULING_CONTAINER,
        HH_OVERDUE_VISITS_CONTAINER,
        HH_PATIENT_NO_SHOWS_CONTAINER,
        HH_PENDING_END_OF_EPISODE_CONTAINER,
        HH_TRANSFERS_CONTAINER,
        HH_DISCHARGE_WITH_PENDING_VISITS_CONTAINER,
        HH_INACTIVE_CLINICIAN_VISITS_CONTAINER,
        HH_NO_FUTURE_APPOINTMENTS_CONTAINER,
        HH_COMPLETED_VISITS_NOT_MAPPED_TO_ORDERS_CONTAINER,
        HH_EVALS_BEFORE_SOCS_CONTAINER,
        HH_MULTIPLE_OASIS_PER_EPISODE_CONTAINER,
        HH_PENDING_REASSESSMENT_CONTAINER,
    ],
};
